import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { UpdateOrganizationKycSettingDto } from '@dealstack/shared/lib/generated';
import {
	getOrganizationKycSetting,
	updateOrganizationKycSetting,
} from '@dealstack/frontend/shared/dataExchange/services/programme-defaults.service.ts';

const queryKey = (organizationId: string) => [
	API_PATHS.PROGRAMME_DEFAULTS_KYC_SETTING_ORGANIZATIONID_GET,
	organizationId,
];

/** Null until the organisation saves for the first time. */
export const useOrganizationKycSetting = (organizationId: string) => {
	return useQuery({
		queryKey: queryKey(organizationId),
		queryFn: () => getOrganizationKycSetting(organizationId),
		enabled: !!organizationId,
		select: (data) => data.payload ?? null,
	});
};

interface UpdateHandlers {
	/** Runs after the refetch has landed, so the caller can release an optimistic overlay. */
	onSuccess?: (body: UpdateOrganizationKycSettingDto) => void;
	onError?: (body: UpdateOrganizationKycSettingDto) => void;
}

/**
 * Handlers belong on the mutation, not on each `mutate` call: in TanStack v5 a per-call callback
 * fires only for the latest call, so overlapping saves lose the earlier one's cleanup.
 */
export const useUpdateOrganizationKycSetting = (
	organizationId: string,
	handlers: UpdateHandlers = {}
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: UpdateOrganizationKycSettingDto) =>
			updateOrganizationKycSetting({ organizationId, body }),
		onSuccess: async (_result, body) => {
			await queryClient.invalidateQueries({ queryKey: queryKey(organizationId) });
			handlers.onSuccess?.(body);
		},
		onError: (_error, body) => handlers.onError?.(body),
	});
};
